<template>
	<AppCardPage class="leningdelen-page">
		<AppCard title="Hypotheek" class="[&_.app-card-content]:gap-4">
			<ExistingMortgageApplyCard
				v-for="em in dossierDataStore.data.existingMortgages"
				:key="em.id"
				:existing-mortgage="em"
				:mortgage-parts="data.mortgage.mortgageParts"
				@apply="(parts) => applyExistingMortgage(em, parts)"
			/>

			<hr />

			<div class="flex flex-col gap-2">
				<AppAutocomplete
					v-model="data.mortgage.lenderCode"
					label="Geldverstrekker"
					empty-message="Geen geldverstrekkers gevonden"
					:disabled="userStore.role !== 'intermediary'"
					:open-on-focus="!data.mortgage.lenderCode"
					:has-icon="true"
					:options="lenderOptions"
					:option-loader="getLenderOption"
				/>

				<IvoProviderWarning
					:receiver-code="selectedReceiverCode"
					:product-code="data.mortgage.productCode"
					:nhg="nhg"
					:has-onderneming="hasOnderneming"
					:has-ivo="hasIvo"
				/>

				<AppAutocomplete
					:key="data.mortgage.lenderCode"
					v-model="data.mortgage.productCode"
					:disabled="
						!data.mortgage.lenderCode || userStore.role !== 'intermediary'
					"
					label="Product"
					empty-message="Geen producten gevonden"
					:open-on-focus="!data.mortgage.productCode"
					:has-icon="false"
					:options="productOptions"
					:option-loader="getProductOption"
				/>

				<AppInput
					v-model="data.mortgage.amountRegistration"
					label="Inschrijving"
					:value-type="ValueType.Currency"
					class="max-w-[200px]"
				/>

				<AppInput
					v-model="data.financieringsOpzet.passeerdatum"
					label="Passeerdatum"
					:value-type="ValueType.Date"
					class="max-w-[200px]"
					:show-display-date="false"
				/>

				<div class="flex flex-col text-sm">
					<label>Soort advies</label>
					<OptionInput
						v-model="data.mortgage.adviceType"
						:options="adviceTypeOptions"
					/>
				</div>
			</div>
		</AppCard>
		<AppCard
			no-padding
			class="overflow-x-auto gap-0 [&_.app-card-content]:gap-0"
		>
			<MortgagePartList
				v-model="data.mortgage.mortgageParts"
				:totals="mortgagePartsTotal"
				:available-mortgage-parts="
					dossierDataStore.data.existingMortgages
						.filter((em) => em.mortgageParts)
						.flatMap((em) => em.mortgageParts!)
				"
				@add-leningdeel="leningdelenStore.addLeningdeel()"
			/>
		</AppCard>

		<AppCard title="Overzicht">
			<div class="overzicht-fields-wrap">
				<RowText
					:read-only="true"
					label="Maximale hypotheek"
					:model-value="computedValues.maxLeningInkomenLasten"
					:value-type="ValueType.Currency"
				/>
				<RowText
					:read-only="true"
					:label="financieringsState.values.resultInfo?.label ?? ''"
					:value-type="ValueType.Currency"
					:model-value="
						financieringsState.values.financiering?.financieringsresultaat
					"
					:class="financieringsState.values.resultInfo?.className"
				/>
				<span v-show="store?.leningdelenTeHoog" class="error-msg">
					Let op: Hoogte leningdelen groter dan maximale hypotheek!</span
				>
			</div>
		</AppCard>
		<AppCard
			v-if="userStore?.user?.role === 'intermediary'"
			title="Optimalisatie"
		>
			<AppButton
				v-if="!store.showOptimalHeights"
				variant="secondary"
				class="optimization-button"
				@click="toggleOptimize"
			>
				Bereken optimale leningdeelhoogtes
			</AppButton>
			<template v-else-if="optimalisatieState.values.error === undefined">
				<RowText
					:read-only="true"
					label="Optimale hoogte leningdeel 1"
					:model-value="optimalisatieState.values.optimaleHoogteDeel1"
					:value-type="ValueType.Currency"
				/>
				<RowText
					:read-only="true"
					label="Optimale hoogte leningdeel 2"
					:model-value="optimalisatieState.values.optimaleHoogteDeel2"
					:value-type="ValueType.Currency"
				/>
				<span class="padded warn-msg"
					>{{ optimalisatieState.values.warning ?? "" }}
				</span>
				<AppButton
					variant="secondary"
					class="optimization-button"
					@click="pasHoogtesToe"
				>
					Optimale hoogtes toepassen
				</AppButton>
			</template>
			<span v-else class="error-msg">{{
				optimalisatieState.values.error
			}}</span>
		</AppCard>
		<AppCard v-if="showValues" class="overflow-hidden">
			<pre>{{ stringifyObject(data.mortgage) }}</pre>
			<br />
			<pre>{{ stringifyObject(mortgagePartsTotal) }}</pre>
			<br />
			<pre>{{ stringifyObject(computedValues) }}</pre>
		</AppCard>
	</AppCardPage>
</template>

<script setup lang="ts">
import { computed, inject, toRef, watch } from "vue";
import IvoProviderWarning from "@/components/ivo/IvoProviderWarning.vue";
import useLeningdelen from "@/store/financiering/tabs/useLeningdelen";
import useFinancieringsOpzet from "@/store/financiering/tabs/useFinancieringsOpzet";
import { useMainStore } from "@/store/useMainStore";
import useOptimalisatie from "@/store/optimalisatie/useOptimalisatie";
import { useUserStore } from "@/store/useUserStore";
import { ValueType } from "@/enums/ValueType";
import { stringifyObject } from "@/helpers/uiHelpers";
import { useLenderProducts } from "@/composables/useLender";
import { useProviderLenders } from "@/composables/useProviderLenders";
import { useIvoDossierContext } from "@/composables/useIvoDossierContext";
import { useDossierDataStore } from "@/store/useDossierDataStore";
import type {
	ExistingMortgage,
	ExistingMortgagePart,
} from "@loanlink-nl/model-app";
import { storeToRefs } from "pinia";
import type { AdviceType } from "@loanlink-nl/model-app";

const adviceTypeOptions: { label: string; value: AdviceType }[] = [
	{ label: "Advies", value: "advice" },
	{ label: "Execution only", value: "executionOnly" },
];

const showValues = inject("DEBUG_SHOW_MODELVALUES", false);

const dossierDataStore = useDossierDataStore();
const { data } = storeToRefs(dossierDataStore);
const leningdelenStore = useLeningdelen();
const { computedValues, mortgagePartsTotal } = storeToRefs(leningdelenStore);
const { state: financieringsState } = useFinancieringsOpzet();
const { state: optimalisatieState, pasHoogtesToe } = useOptimalisatie();

const store = useMainStore();
const userStore = useUserStore();

const { lenderOptions, getLenderOption } = useProviderLenders();
const { productOptions, getProductOption } = useLenderProducts(
	toRef(() => data.value.mortgage.lenderCode),
);

const {
	hasOnderneming: dossierHasOnderneming,
	hasIvo: dossierHasIvo,
	nhg: dossierNhg,
} = useIvoDossierContext();
const hasOnderneming = computed(() => dossierHasOnderneming.value);
const hasIvo = computed(() => dossierHasIvo.value);
const nhg = computed(() => {
	return dossierNhg.value;
});
const selectedReceiverCode = computed(
	() =>
		getLenderOption(data.value.mortgage.lenderCode)?.lender.provider
			.receiverCode,
);

watch(
	() => data.value.mortgage.lenderCode,
	(providerCode, oldProviderCode) => {
		if (oldProviderCode === undefined) return;
		if (!providerCode || providerCode !== oldProviderCode) {
			data.value.mortgage.productCode = undefined;
			for (const part of data.value.mortgage.mortgageParts) {
				part.productCode = undefined;
			}
		}
	},
);

function toggleOptimize() {
	if (userStore.user?.role === "intermediary") {
		store.showOptimalHeights = !store.showOptimalHeights;
	}
}

function applyExistingMortgage(
	existingMortgage: ExistingMortgage,
	parts: ExistingMortgagePart[],
) {
	data.value.mortgage.lenderCode = existingMortgage.lenderCode;
	data.value.mortgage.productCode = existingMortgage.productCode;

	for (const part of parts) {
		leningdelenStore.addLeningdeel({
			existingMortgagePartId: part.id,
			amount: part.amountCurrent,
			repaymentForm: part.repaymentForm,

			endDate: part.endDate,
			fixedInterestEndDate: part.fixedInterestEndDate,

			interestRate: part.interestRate,

			interestDeductionLaw: part.interestDeductionLaw,
			interestDeductionEndDate: part.interestDeductionEndDate,
		});
	}
}
</script>

<style lang="scss">
@use "@/style/mixins.scss" as mixins;

.error-msg {
	color: var(--color-fail);
	text-align: center;
}

.warn-msg {
	color: var(--color-warn);
}

.padded {
	margin-left: 12px;
}

.leningdelen-page {
	th .table-cell-flex {
		align-items: flex-end;
	}

	th:last-child,
	td:last-child {
		padding-right: 20px;
	}

	td:first-child {
		padding-left: 12px;
	}
}

.optimization-button {
	align-self: start;
}

div[data-type="autocomplete"] {
	font-size: 0.85rem;

	.formkit-label {
		font-weight: normal;
	}

	.formkit-inner {
		display: flex;
		flex-direction: row;
		position: relative;
		width: 300px;
		background: none;
		border: 1px solid var(--border);
		border-radius: 0.25rem;

		&:focus-within {
			@include mixins.native-focus;
		}

		input {
			background: none;
			width: 100%;
			border: none;
			padding: 0.25rem 0.5rem;
			color: inherit;
			font-family: inherit;

			&:focus {
				outline: none;
			}
		}

		.formkit-listbox-button,
		.formkit-remove-selection {
			width: 2.5em;
			display: flex;
			justify-content: center;
			align-items: center;

			span {
				width: 100%;
				display: flex;
				justify-content: center;

				svg {
					width: 1em;
				}
			}
		}

		.formkit-dropdown-wrapper {
			background: white;
			border: 1px solid var(--border);
			border-radius: 0.25rem;
			display: none;

			li {
				padding: 0.25rem 0.5rem;

				&[data-is-active="true"] {
					background: var(--background-secondary);
					outline: 1px solid var(--border);
				}
			}
		}
	}

	&[data-empty="true"] {
		.formkit-selections {
			display: none;
		}
	}

	.formkit-selections {
		position: absolute;
		width: 100%;
		height: 100%;
		background: var(--background-secondary);
		border-radius: 4px;
		z-index: 2;
		overflow: hidden;

		.formkit-selection-wrapper {
			display: flex;
			height: 100%;
			justify-content: space-between;
			padding: 0.25rem 0;
			padding-left: 0.5rem;
			min-width: 0;

			.formkit-selection {
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		}
	}

	&[data-expanded="true"] {
		.formkit-dropdown-wrapper {
			display: block;
		}
	}

	&[data-disabled="true"] {
		.formkit-inner {
			background-color: var(--background-secondary);
		}
	}
}
</style>
